<template>
    <AddFileSection
        :id="AddFileFormSectionIds.FILE_DETAILS"
        sectionHeading="File details"
    >
        <div class="mt-3 grid grid-cols-12 gap-4">
            <DsTextInput
                v-model.trim="addFileStore.refNumber"
                :id="FileDetailErrorKeys.REF_NUMBER"
                class="col-span-12 sm:col-span-6"
                :disabled="!!addFileStore.tpsOrder"
                :error="fileDetailErrors[FileDetailErrorKeys.REF_NUMBER]"
                label="File reference #"
                :name="FileDetailErrorKeys.REF_NUMBER"
                required
                type="text"
                @focusout="refNumberToCheck = addFileStore.refNumber"
                @input="clearServerValidationError(FileDetailErrorKeys.REF_NUMBER)"
            />
            <DsAddressAutocompleteSearch
                v-model="addFileStore.address"
                :id="FileDetailErrorKeys.ADDRESS"
                class="col-span-12 sm:col-span-6"
                :disabled="!!addFileStore.tpsOrder"
                :error="fileDetailErrors[FileDetailErrorKeys.ADDRESS]"
                label="Property address"
                :name="FileDetailErrorKeys.ADDRESS"
                required
                @input="clearServerValidationError(FileDetailErrorKeys.ADDRESS)"
            />
            <!-- @todo [CLENG-1942] remove hidden inputs needed for form submission -->
            <template v-if="addFileStore.tpsOrder">
                <input
                    :name="FileDetailErrorKeys.REF_NUMBER"
                    type="hidden"
                    :value="addFileStore.tpsOrder.ref_number"
                />
                <input
                    :name="FileDetailErrorKeys.ADDRESS"
                    type="hidden"
                    :value="addFileStore.tpsOrder.address"
                />
            </template>
            <SelectCompanyBranch
                v-model:branch="addFileStore.branch"
                v-model:company="addFileStore.company"
                :branchError="fileDetailErrors[FileDetailErrorKeys.BRANCH_ID]"
                :branches="userStore.activeBranches"
                :companies="userStore.activeCompanies"
                :companyError="fileDetailErrors[FileDetailErrorKeys.COMPANY_ID]"
                :disableBranch="!!addFileStore.tpsOrder?.branch_id"
                :disableCompany="!!addFileStore.tpsOrder?.title_id"
                :initialBranchId="addFileStore.tpsOrder?.branch_id ?? userStore.defaultWireDocBranch?.id"
                :initialCompanyId="addFileStore.tpsOrder?.title_id ?? userStore.defaultWireDocCompany?.id"
                @fieldInput="clearServerValidationError($event)"
            />
            <div
                v-if="userStore.showCloseDate"
                class="col-span-12 sm:col-span-6 md:col-span-4"
            >
                <DsDatePicker
                    v-model="addFileStore.closeDate"
                    v-model:openPicker="isCloseDatePickerOpen"
                    :input="{
                        label: 'Close date',
                        required: false,
                        error: fileDetailErrors[FileDetailErrorKeys.CLOSE_DATE],
                    }"
                    :maxValue="maxCloseDate"
                    :minValue="minCloseDate"
                    @update:modelValue="clearServerValidationError(FileDetailErrorKeys.CLOSE_DATE)"
                />
            </div>
            <!-- @todo [CLENG-1942] remove hidden inputs needed for form submission -->
            <input
                :id="FileDetailErrorKeys.CLOSE_DATE"
                :name="FileDetailErrorKeys.CLOSE_DATE"
                type="hidden"
                :value="formattedCloseDate"
            />
            <DsMoneyInput
                v-if="userStore.showBalanceDue"
                v-model="addFileStore.balanceDue"
                class="col-span-12 sm:col-span-6 md:col-span-4"
                :error="fileDetailErrors[FileDetailErrorKeys.BALANCE_DUE]"
                label="Balance due"
                :moneyConfig="{ prefix: '$', disableNegative: true }"
                :name="FileDetailErrorKeys.BALANCE_DUE"
                type="text"
                @input="clearServerValidationError(FileDetailErrorKeys.BALANCE_DUE)"
            />
            <!-- @todo [CLENG-1942] remove hidden inputs needed for form submission -->
            <input
                v-if="userStore.showBalanceDue"
                :id="FileDetailErrorKeys.BALANCE_DUE"
                :name="FileDetailErrorKeys.BALANCE_DUE"
                type="hidden"
                :value="formattedBalanceDue"
            />
            <DsTextarea
                v-if="userStore.showNotes"
                v-model="addFileStore.notes"
                :id="FileDetailErrorKeys.NOTES"
                class="col-span-12"
                :error="fileDetailErrors[FileDetailErrorKeys.NOTES]"
                helperText="Notes added here are visible to both your internal team and all client contacts listed on the file."
                label="Add file note"
                :name="FileDetailErrorKeys.NOTES"
                @input="clearServerValidationError(FileDetailErrorKeys.NOTES)"
            />
            <!-- @todo [CLENG-1942] remove hidden inputs needed for form submission -->
            <input
                v-if="userStore.showNotes"
                :name="FileDetailErrorKeys.NOTES"
                type="hidden"
                :value="addFileStore.notes"
            />
        </div>
        <AddFileConfirmationDialog v-model:open="isConfirmationOpen" />
        <slot />
    </AddFileSection>
</template>

<script setup lang="ts">
import { FileDetailErrorKeys } from '@/company-portal/add-file/add-file-form-errors.types';
import { registerAddFileSubmittedAction } from '@/company-portal/add-file/add-file-reset';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import { AddFileFormSectionIds } from '@/company-portal/add-file/add-file.types';
import { clearServerValidationError, serverValidationErrors } from '@/company-portal/add-file/submission-error';
import DsDatePicker from '@/components/design-system/form/date-picker/DsDatePicker.vue';
import DsAddressAutocompleteSearch from '@/components/design-system/form/input/DsAddressAutocomplete/DsAddressAutocompleteSearch.vue';
import DsMoneyInput from '@/components/design-system/form/input/DsMoneyInput.vue';
import DsTextarea from '@/components/design-system/form/input/text/DsTextarea.vue';
import DsTextInput from '@/components/design-system/form/input/text/DsTextInput.vue';
import AddFileSection from '@/components/Views/Company/AddFile/AddFileSection.vue';
import AddFileConfirmationDialog from '@/components/Views/Company/AddFile/ConfirmationDialog/AddFileConfirmationDialog.vue';
import SelectCompanyBranch from '@/components/Views/Company/shared/SelectCompanyBranch.vue';
import { useUniqueFileRefNumber } from '@/shared/composables/use-unique-file-ref';
import { calendarDateToNativeDate, createCalendarDate } from '@/shared/utils/calendar-date-utils';
import { formatDate } from '@/shared/utils/format-date';
import { formatCentsAsMoney } from '@/shared/utils/format-number';
import { useUserStore } from '@/stores/company/user.store';
import { getLocalTimeZone, today } from '@internationalized/date';
import { computed, onUnmounted, ref, watch, watchEffect } from 'vue';

const addFileStore = useAddFileStore();
const userStore = useUserStore();

watchEffect(() => {
    addFileStore.refNumber = addFileStore.tpsOrder?.ref_number ?? '';
    addFileStore.address = addFileStore.tpsOrder?.address ?? '';
    const closeDateStr = addFileStore.tpsOrder?.close_date;
    if (closeDateStr) {
        const [month, day, year] = closeDateStr.split('/').map(Number);
        addFileStore.closeDate = month && day && year ? createCalendarDate(year, month, day) : undefined;
    }
    addFileStore.notes = addFileStore.tpsOrder?.notes ?? '';
});

const isConfirmationOpen = ref(false);

const isCloseDatePickerOpen = ref(false);

const minCloseDate = computed(() => today(getLocalTimeZone()).subtract({ years: 5 }));
const maxCloseDate = computed(() => today(getLocalTimeZone()).add({ days: 90 }));

const formattedCloseDate = computed(() => {
    if (!addFileStore.closeDate) return '';
    return formatDate(calendarDateToNativeDate(addFileStore.closeDate));
});

const formattedBalanceDue = computed(() => {
    if (!addFileStore.balanceDue) return '';
    return formatCentsAsMoney(addFileStore.balanceDue);
});

const fileDetailErrors = computed(() => ({
    [FileDetailErrorKeys.REF_NUMBER]: serverValidationErrors.value?.[FileDetailErrorKeys.REF_NUMBER]?.join('') ?? '',
    [FileDetailErrorKeys.ADDRESS]: serverValidationErrors.value?.[FileDetailErrorKeys.ADDRESS]?.join('') ?? '',
    [FileDetailErrorKeys.COMPANY_ID]: serverValidationErrors.value?.[FileDetailErrorKeys.COMPANY_ID]?.join('') ?? '',
    [FileDetailErrorKeys.BRANCH_ID]: serverValidationErrors.value?.[FileDetailErrorKeys.BRANCH_ID]?.join('') ?? '',
    [FileDetailErrorKeys.CLOSE_DATE]: serverValidationErrors.value?.[FileDetailErrorKeys.CLOSE_DATE]?.join('') ?? '',
    [FileDetailErrorKeys.BALANCE_DUE]: serverValidationErrors.value?.[FileDetailErrorKeys.BALANCE_DUE]?.join('') ?? '',
    [FileDetailErrorKeys.NOTES]: serverValidationErrors.value?.[FileDetailErrorKeys.NOTES]?.join('') ?? '',
}));

const { refNumberToCheck, refNumberIsUnique } = useUniqueFileRefNumber();

watch(refNumberIsUnique, value => {
    console.log('[uniqueRefNumberCheck] AddFile refNumberIsUnique:', value, 'for', refNumberToCheck.value);
});

const unregisterAddFileSubmittedAction = registerAddFileSubmittedAction(addFileStore.resetAddFileStore);

onUnmounted(unregisterAddFileSubmittedAction);
</script>
